/**
 * Card, label, alert, badge, spinner and skeleton primitives.
 *
 * They are grouped in one module because each is a few lines of markup and they are always used together; a
 * separate file per element would add ten files without adding a decision.
 */

import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';

/** Surface that groups related content. */
export const Card = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('card-surface', className)} {...props} />
  ),
);

Card.displayName = 'Card';

/** Header region of a {@link Card}. */
export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1 p-6 pb-4', className)} {...props} />;
}

/** Title of a {@link Card}. */
export function CardTitle({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  // `children` is destructured rather than left inside the spread so the heading's content is visible in the
  // signature; the rendered output is the same either way.
  return (
    <h3 className={cn('text-lg font-semibold leading-none', className)} {...props}>
      {children}
    </h3>
  );
}

/** Supporting sentence of a {@link Card}. */
export function CardDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

/** Body of a {@link Card}. */
export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6 pt-0', className)} {...props} />;
}

/** Footer of a {@link Card}. */
export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center gap-3 p-6 pt-0', className)} {...props} />;
}

/** Form label wired to its control through `htmlFor`. */
export const Label = forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
      className,
    )}
    {...props}
  />
));

Label.displayName = 'Label';

const alertVariants = cva('flex items-start gap-3 rounded-md border p-4 text-sm', {
  variants: {
    variant: {
      info: 'border-border bg-muted text-foreground',
      success: 'border-success/30 bg-success/10 text-foreground',
      warning: 'border-warning/40 bg-warning/10 text-foreground',
      destructive: 'border-destructive/40 bg-destructive/10 text-foreground',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

/** Props accepted by {@link Alert}. */
export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

/**
 * Inline message.
 *
 * `role="alert"` is used for the destructive and warning variants so assistive technology announces a failure
 * immediately; informational messages stay silent.
 */
export function Alert({ className, variant = 'info', ...props }: AlertProps) {
  const isAssertive = variant === 'destructive' || variant === 'warning';

  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      role={isAssertive ? 'alert' : 'status'}
      {...props}
    />
  );
}

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary-muted text-accent-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'border-border text-foreground',
        success: 'border-transparent bg-success/15 text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/** Props accepted by {@link Badge}. */
export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

/** Small status label. */
export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/** Indeterminate progress indicator; `aria-hidden` because the surrounding text carries the meaning. */
export function Spinner({ className }: { className?: string }) {
  return <Loader2 aria-hidden className={cn('h-4 w-4 animate-spin', className)} />;
}

/** Placeholder block shown while content loads. */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} {...props} />;
}
